import { useEffect, useRef, useState, type TouchEvent } from "react";
import { fetchNewsCards, type NewsCard } from "../api/tcg";
import "./newsCards.component.css";

const MAX_VISIBLE_COUNT = 3;

function getVisibleCount(): number {
    if (typeof window === "undefined") return MAX_VISIBLE_COUNT;
    if (window.innerWidth <= 600) return 1;
    if (window.innerWidth <= 1080) return 2;
    return MAX_VISIBLE_COUNT;
}

function isExternalLink(url: string): boolean {
    return /^https?:\/\//i.test(url);
}

function instagramPermalink(url: string | null | undefined): string | null {
    if (!url) return null;
    const match = url.match(/instagram\.com\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i);
    if (!match) return null;
    const type = match[1].toLowerCase() === "reels" ? "reel" : match[1].toLowerCase();
    return `https://www.instagram.com/${type}/${match[2]}/`;
}

function instagramEmbedUrl(permalink: string): string {
    return `${permalink}embed/captioned/`;
}

function getInstagramPermalink(card: NewsCard): string | null {
    return (
        instagramPermalink(card.instagram_url) ??
        instagramPermalink(card.image_url) ??
        instagramPermalink(card.link_url)
    );
}

function NewsPlaceholder() {
    return (
        <div className="news-card-placeholder" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
                <path d="M5 5.5h14v13H5z" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 9h8M8 12h8M8 15h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        </div>
    );
}

function NewsImage({ src }: { src: string | null | undefined }) {
    const [failed, setFailed] = useState(false);
    const imageUrl = src?.trim() ?? "";

    useEffect(() => {
        setFailed(false);
    }, [imageUrl]);

    if (!imageUrl || failed) return <NewsPlaceholder />;

    return (
        <img
            src={imageUrl}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
        />
    );
}

function InstagramCard({ id, permalink }: { id: number; permalink: string }) {
    return (
        <article className="news-card news-card-instagram" key={id}>
            <iframe
                className="news-card-instagram-frame"
                title={`Instagram Beitrag ${id}`}
                src={instagramEmbedUrl(permalink)}
                loading="lazy"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
            />
        </article>
    );
}

function NewsCardsComponent() {
    const [cards, setCards] = useState<NewsCard[]>([]);
    const [index, setIndex] = useState(0);
    const [visibleCount, setVisibleCount] = useState(getVisibleCount);
    const touchStartX = useRef<number | null>(null);

    useEffect(() => {
        let isMounted = true;

        fetchNewsCards()
            .then((items) => {
                if (!isMounted) return;
                const visible = items
                    .filter((card) => card.is_visible)
                    .sort((a, b) => a.position - b.position);
                setCards(visible);
                setIndex(0);
            })
            .catch(() => {
                if (isMounted) setCards([]);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setVisibleCount(getVisibleCount());
            setIndex(0);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    if (cards.length === 0) return null;

    const activeVisibleCount = Math.min(visibleCount, cards.length);
    const maxIndex = Math.max(0, cards.length - activeVisibleCount);
    const prev = () => setIndex((current) => Math.max(0, current - 1));
    const next = () => setIndex((current) => Math.min(maxIndex, current + 1));

    const onTouchStart = (event: TouchEvent) => {
        touchStartX.current = event.touches[0].clientX;
    };

    const onTouchEnd = (event: TouchEvent) => {
        if (touchStartX.current === null) return;
        const diff = touchStartX.current - event.changedTouches[0].clientX;
        if (diff > 40) next();
        else if (diff < -40) prev();
        touchStartX.current = null;
    };

    return (
        <div className="news-cards">
            <div className="news-cards-header">
                <div>
                    <span className="news-cards-eyebrow">News & Socials</span>
                    <p className="news-cards-subtitle">Aktuelle Highlights aus dem TCG Awards Umfeld</p>
                </div>

                {cards.length > activeVisibleCount && (
                    <div className="news-cards-controls">
                        <button
                            className="news-cards-nav"
                            onClick={prev}
                            disabled={index === 0}
                            aria-label="Vorherige News"
                            type="button"
                        >
                            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>

                        <button
                            className="news-cards-nav"
                            onClick={next}
                            disabled={index >= maxIndex}
                            aria-label="Naechste News"
                            type="button"
                        >
                            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>

            <div
                className="news-cards-track-wrapper"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
            >
                <div
                    className="news-cards-track"
                    style={{ transform: `translateX(calc(-${index} * (100% / ${activeVisibleCount}) - ${index} * var(--news-gap) / ${activeVisibleCount}))` }}
                >
                    {cards.map((card) => {
                        const instagramUrl = getInstagramPermalink(card);

                        if (instagramUrl) {
                            return <InstagramCard key={card.id} id={card.id} permalink={instagramUrl} />;
                        }

                        const linkUrl = card.link_url?.trim();

                        return (
                            <article className="news-card" key={card.id}>
                                <div className="news-card-image">
                                    <NewsImage src={card.image_url} />
                                </div>

                                <div className="news-card-body">
                                    <h3>{card.title}</h3>
                                    {card.description && <p>{card.description}</p>}

                                    {linkUrl && (
                                        <a
                                            className="news-card-link"
                                            href={linkUrl}
                                            target={isExternalLink(linkUrl) ? "_blank" : undefined}
                                            rel={isExternalLink(linkUrl) ? "noopener noreferrer" : undefined}
                                        >
                                            {card.link_label?.trim() || "Mehr erfahren"}
                                        </a>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>

            {cards.length > activeVisibleCount && (
                <div className="news-cards-dots">
                    {cards.map((card, dotIndex) => (
                        <button
                            key={card.id}
                            className={`news-cards-dot ${dotIndex >= index && dotIndex < index + activeVisibleCount ? "is-active" : ""}`}
                            onClick={() => setIndex(Math.min(dotIndex, maxIndex))}
                            aria-label={`News ${dotIndex + 1} anzeigen`}
                            type="button"
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default NewsCardsComponent;